import * as AlertDialog from "@radix-ui/react-alert-dialog";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  approveServiceWeek,
  fetchServiceWeekTargets,
  includeServiceWeekCandidate,
  type CurrentServiceWeekReview,
  type FrozenServiceWeekReview,
  type ServiceWeekCoverageFact,
  type ServiceWeekReceipt,
  type ServiceWeekReview,
  type ServiceWeekReviewHeader,
  type ServiceWeekSummary,
  type ServiceWeekTargets,
} from "../data/service-week-approval";
import {
  addServiceWeeks,
  currentServiceWeekStart,
  formatServiceWeekInstant,
  formatServiceWeekRange,
  inProgressServiceWeekCopy,
  latestCompletedServiceWeekStart,
  recordedServiceDuration,
  serviceWeekChangeCopy,
  serviceCheckpointCopy,
  serviceExceptionCopy,
  serviceProvenanceCopy,
  serviceSummaryLabels,
  serviceWeekCopy,
  serviceWeekReasonCopy,
} from "../lib/service-week";
import { fetchServiceWeekEntryReview, markServiceWeekEntriesReviewed, recordServiceWeekEntryDecision, type ServiceWeekEntryReview, type ServiceWeekEntry, type ServiceEntryAct } from "../data/service-week-decisions";
import { ServiceWeekEntryDecisionDialog, type ServiceEntryDialogSelection } from "./ServiceWeekEntryDecisionDialog";
import { ServiceWeekDecisionTrail } from "./ServiceWeekDecisionTrail";
import { useSupportSession } from "../support/SupportSessionProvider";
import type { TimesheetsPreselect } from "../lib/schedule-timesheets-route";

type Target = Extract<ServiceWeekTargets, { status: "ok" }>["targets"][number];
type Facts = CurrentServiceWeekReview | FrozenServiceWeekReview;
type Busy = "targets" | "review" | "include" | "approve" | "entry" | "clean" | null;
type ConfirmationAct = { kind: "clean" } | { kind: "approve" } | { kind: "include"; candidateRef: string };
type Confirmation = (ConfirmationAct & { requestGeneration: number }) | null;
type RecordedReceipt = { receipt: ServiceWeekReceipt; header: ServiceWeekReviewHeader };

/** This gate is only an optimization. Every RPC independently binds the direct actor. */
export function ServiceWeekApprovalDesk({ client, actorId, accountId, accountRole, canReview, active, preselect = null, onPreselectConsumed }: {
  client: SupabaseClient;
  actorId: string | null;
  accountId: string;
  accountRole: string | null;
  canReview: boolean;
  active: boolean;
  preselect?: TimesheetsPreselect | null;
  onPreselectConsumed?: () => void;
}) {
  const support = useSupportSession();
  const eligible = Boolean(actorId) && (accountRole === "admin" || accountRole === "supervisor") && canReview;
  if (support.loading) {
    return active ? <p role="status" className="surface__state">{serviceWeekCopy.loadingTargets}</p> : null;
  }
  if (!eligible || support.session !== null) {
    return active ? <p role="status" className="surface__state">{serviceWeekCopy.unavailable}</p> : null;
  }
  // An actor/permission/support/account change unmounts all facts, refs and receipts immediately.
  return <ApprovalWorkspace
    key={`${actorId}:${accountId}:${accountRole}`}
    client={client}
    accountId={accountId}
    active={active}
    preselect={preselect ?? null}
    {...(onPreselectConsumed ? { onPreselectConsumed } : {})}
  />;
}

function ApprovalWorkspace({ client, accountId, active, preselect, onPreselectConsumed }: {
  client: SupabaseClient;
  accountId: string;
  active: boolean;
  preselect: TimesheetsPreselect | null;
  onPreselectConsumed?: () => void;
}) {
  const [targets, setTargets] = useState<ServiceWeekTargets | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [site, setSite] = useState<Target | null>(null);
  const [weekStart, setWeekStart] = useState<string | null>(null);
  const [entryReview, setEntryReview] = useState<ServiceWeekEntryReview | null>(null);
  const review = entryReview?.status === "unavailable" ? entryReview : entryReview?.week ?? null;
  const [entrySelection, setEntrySelection] = useState<ServiceEntryDialogSelection | null>(null);
  const [entryError, setEntryError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation>(null);
  const [committedReceipt, setCommittedReceipt] = useState<RecordedReceipt | null>(null);
  const generation = useRef(0);
  const retainedReview = useRef<ServiceWeekEntryReview | null>(null);
  const busyRef = useRef<Busy>(null);
  const opener = useRef<HTMLButtonElement | null>(null);
  const refreshButton = useRef<HTMLButtonElement | null>(null);
  const cancelButton = useRef<HTMLButtonElement | null>(null);
  const activeRef = useRef(active);
  activeRef.current = active;

  const setPending = useCallback((value: Busy) => {
    busyRef.current = value;
    setBusy(value);
  }, []);
  const acceptReview = useCallback((next: ServiceWeekEntryReview) => { retainedReview.current = next; setEntryReview(next); }, []);
  const clearFacts = useCallback((keepReceipt = false) => {
    generation.current += 1;
    retainedReview.current = null;
    setEntryReview(null);
    setConfirmation(null);
    setEntrySelection(null);
    setEntryError(null);
    if (!keepReceipt) setCommittedReceipt(null);
  }, []);

  const loadTargets = useCallback(async () => {
    if (!activeRef.current || document.visibilityState === "hidden" || busyRef.current !== null) return;
    clearFacts();
    setTargets(null);
    setMessage(null);
    setPending("targets");
    const request = generation.current;
    const next = await fetchServiceWeekTargets(client, accountId);
    if (!activeRef.current || request !== generation.current) return;
    setTargets(next);
    setPending(null);
  }, [accountId, client, clearFacts, setPending]);

  const loadReview = useCallback(async (keepReceipt = false) => {
    if (!activeRef.current || site === null || weekStart === null) return;
    clearFacts(keepReceipt);
    if (document.visibilityState === "hidden") {
      setPending(null);
      return;
    }
    setPending("review");
    const request = generation.current;
    const next = await fetchServiceWeekEntryReview(client, { accountId, clientId: site.clientId, siteId: site.siteId, weekStart });
    if (!activeRef.current || request !== generation.current) return;
    acceptReview(next);
    // A refused target cannot retain a receipt. A transport failure after a committed
    // approval may keep that act separately, but it never restores the former facts.
    const returnedWeek = next.status === "unavailable" ? next : next.week;
    if (returnedWeek.status !== "unavailable" || returnedWeek.reason === "target_unavailable") setCommittedReceipt(null);
    setPending(null);
  }, [accountId, client, site, weekStart, clearFacts, setPending, acceptReview]);

  useLayoutEffect(() => {
    // React StrictMode replays setup after cleanup. Restore activation and
    // release the disposed request's busy latch before restarting discovery.
    activeRef.current = active;
    busyRef.current = null;
    setBusy(null);
    if (!active) {
      clearFacts();
      setPending(null);
      setMessage(null);
      return;
    }
    if (site !== null && weekStart !== null) void loadReview();
    return () => { generation.current += 1; };
  }, [active, site, weekStart, loadReview, clearFacts, setPending]);

  useEffect(() => {
    if (active && targets === null) void loadTargets();
  }, [active, targets, loadTargets]);

  // Apply a deep-link preselect only when the caller's own targets list already contains
  // that client/site. A foreign or absent target stays indistinguishable (no preselect).
  useEffect(() => {
    if (!active || preselect === null || targets === null) return;
    onPreselectConsumed?.();
    if (targets.status !== "ok") return;
    const match = targets.targets.find(
      (target) => target.clientId === preselect.clientId && target.siteId === preselect.siteId,
    );
    if (!match) return;
    clearFacts();
    setMessage(null);
    setClientId(match.clientId);
    setSite(match);
    setWeekStart(preselect.weekStart);
  }, [active, preselect, targets, clearFacts, onPreselectConsumed]);

  useEffect(() => () => { generation.current += 1; retainedReview.current = null; activeRef.current = false; }, []);

  const refresh = useCallback(() => {
    if (!activeRef.current || busyRef.current !== null) return;
    setMessage(null);
    if (site !== null && weekStart !== null) void loadReview(committedReceipt !== null);
    else void loadTargets();
  }, [loadReview, loadTargets, site, weekStart, committedReceipt]);

  useEffect(() => {
    let backgrounded = document.visibilityState === "hidden";
    const blur = () => {
      backgrounded = true;
      clearFacts();
      setPending(null);
      setMessage(null);
    };
    const focus = () => {
      if (document.visibilityState === "hidden" || !backgrounded) return;
      backgrounded = false;
      refresh();
    };
    const visibility = () => {
      if (document.visibilityState === "hidden") blur();
      else focus();
    };
    window.addEventListener("blur", blur);
    window.addEventListener("focus", focus);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      window.removeEventListener("blur", blur);
      window.removeEventListener("focus", focus);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [refresh, clearFacts, setPending]);

  const targetRows = targets?.status === "ok" ? targets.targets : [];
  const clients = Array.from(new Map(targetRows.map((target) => [target.clientId, target])).values());
  const sites = targetRows.filter((target) => target.clientId === clientId);
  const currentWeek = site === null ? null : currentServiceWeekStart(site.timeZone);
  const facts = review?.status === "ok"
    ? review.verdict === "stale" ? review.currentStatus === "reviewable" ? review.currentReview : null : review.review
    : null;
  const current = facts?.basis === "current_candidates" ? facts : null;
  const revision = review?.status === "ok" && !(review.verdict === "stale" && review.currentStatus === "unavailable")
    ? review.reviewRevision : null;

  async function confirmAction() {
    if (!activeRef.current || document.visibilityState === "hidden" || busyRef.current !== null || confirmation === null || confirmation.requestGeneration !== generation.current || retainedReview.current !== entryReview || site === null || weekStart === null || revision === null || entryReview?.status !== "ok") return;
    const action = confirmation;
    const key = { accountId, clientId: site.clientId, siteId: site.siteId, weekStart };
    if (action.kind === "approve" && (current === null || !current.approvalEligibility.canApprove)) return;
    if (action.kind === "include" && (current === null || !current.coverage.some((row) => row.canInclude && row.candidateRef === action.candidateRef))) return;
    if (action.kind === "clean" && entryReview.cleanEntryRefs.length === 0) return;
    setPending(action.kind);
    setMessage(null);
    const request = generation.current;
    if (action.kind === "clean") {
      const result = await markServiceWeekEntriesReviewed(client, key, entryReview.cleanEntryRefs, revision, crypto.randomUUID());
      if (!activeRef.current || request !== generation.current) return;
      if (result.status === "unavailable") { clearFacts(); acceptReview(result); setPending(null); return; }
      setMessage(result.status === "ok" ? serviceWeekCopy.reviewedRecorded : serviceWeekReasonCopy(result.reason, site.timeZone));
      await loadReview();
    } else if (action.kind === "include") {
      const result = await includeServiceWeekCandidate(client, key, action.candidateRef, revision);
      if (!activeRef.current || request !== generation.current) return;
      setConfirmation(null);
      if (result.status === "ok") {
        await loadReview();
      } else if (result.status === "conflict" || result.status === "refused") {
        setMessage(serviceWeekReasonCopy(result.reason, site.timeZone));
        await loadReview();
      } else {
        clearFacts();
        acceptReview(result);
        setPending(null);
      }
    } else {
      if (current === null) { setPending(null); return; }
      const result = await approveServiceWeek(client, key, revision);
      if (!activeRef.current || request !== generation.current) return;
      setConfirmation(null);
      if (result.status === "approved") {
        setCommittedReceipt({ receipt: result.receipt, header: {
          providerLabel: current.providerLabel, clientLabel: current.clientLabel,
          siteLabel: current.siteLabel, timeZone: current.timeZone,
          weekStart: current.weekStart, weekEndExclusive: current.weekEndExclusive,
        } });
        await loadReview(true);
      } else if (result.status === "conflict" || result.status === "refused") {
        setMessage(serviceWeekReasonCopy(result.reason, site.timeZone));
        await loadReview();
      } else {
        clearFacts();
        acceptReview(result);
        setPending(null);
      }
    }
  }

  function returnFocus() {
    (opener.current?.isConnected ? opener.current : refreshButton.current)?.focus();
  }

  function openEntry(act: ServiceEntryAct, entry: ServiceWeekEntry, button: HTMLButtonElement) {
    if (!activeRef.current || document.visibilityState === "hidden" || busyRef.current !== null || entryReview?.status !== "ok" || retainedReview.current !== entryReview || facts === null) return;
    const row = facts.coverage.find((item) => item.rowOrdinal === entry.coverageRowOrdinal);
    if (!row || (act === "reject" ? !entry.canReject : !entry.canSet)) return;
    opener.current = button;
    setEntryError(null);
    setEntrySelection({ act, entry, serviceLabel: row.serviceLabel, serviceDay: row.serviceDay, requestGeneration: ++generation.current });
  }

  async function submitEntry(reason: string, boundaryLocal: string | null) {
    if (!activeRef.current || document.visibilityState === "hidden" || busyRef.current !== null || entrySelection === null || entrySelection.requestGeneration !== generation.current || retainedReview.current !== entryReview || entryReview?.status !== "ok" || site === null || weekStart === null || revision === null) return;
    const { act, entry } = entrySelection;
    const freshEntry = entryReview.entries.find((item) => item.entryRef === entry.entryRef);
    if (!freshEntry || (act === "reject" ? !freshEntry.canReject : !freshEntry.canSet)) return;
    const request = generation.current;
    setPending("entry"); setEntryError(null); setMessage(null);
    const result = await recordServiceWeekEntryDecision(client, { accountId, clientId: site.clientId, siteId: site.siteId, weekStart }, {
      entryRef: entry.entryRef, expectedReviewRevision: revision, requestId: crypto.randomUUID(), act, reason, boundaryLocal,
    });
    if (!activeRef.current || request !== generation.current) return;
    if (result.status === "refused" && (result.reason === "invalid_boundary" || result.reason === "reason_required")) {
      setEntryError(serviceWeekReasonCopy(result.reason)); setPending(null); return;
    }
    if (result.status === "unavailable") { clearFacts(); acceptReview(result); setPending(null); return; }
    setMessage(result.status === "ok" ? serviceWeekCopy.decisionRecorded : serviceWeekReasonCopy(result.reason, site.timeZone));
    await loadReview();
  }

  function closeEntry() {
    // Closing also disposes pending callbacks and their transport references.
    clearFacts(); setPending(null); setMessage(null); void loadReview();
  }

  function openConfirmation(next: ConfirmationAct, button: HTMLButtonElement) {
    if (!activeRef.current || document.visibilityState === "hidden" || busyRef.current !== null || entryReview?.status !== "ok" || retainedReview.current !== entryReview || revision === null) return;
    opener.current = button;
    setConfirmation({ ...next, requestGeneration: ++generation.current });
  }

  if (!active) return null;
  const pending = busy !== null;
  return (
    <section className="service-week" aria-label="Service-week approval">
      <div className="service-week__head">
        <div><h3>Service-week approval</h3><p className="page__note">Review the complete site-week, including exceptions, and freeze its service facts.</p></div>
        <button ref={refreshButton} type="button" className="builder__navbtn" disabled={pending} onClick={refresh}>Refresh</button>
      </div>
      {targets?.status === "ok" ? (
        <div className="service-week__controls">
          <label>Client<select value={clients.findIndex((target) => target.clientId === clientId)} disabled={pending} onChange={(event) => {
            clearFacts(); setMessage(null); setClientId(clients[Number(event.target.value)]?.clientId ?? null); setSite(null); setWeekStart(null);
          }}><option value={-1}>Choose client</option>{clients.map((target, index) => <option key={target.clientId} value={index}>{target.clientLabel}</option>)}</select></label>
          <label>Site<select value={sites.findIndex((target) => target.siteId === site?.siteId)} disabled={pending || clientId === null} onChange={(event) => {
            clearFacts(); setMessage(null);
            const next = sites[Number(event.target.value)] ?? null;
            setSite(next); setWeekStart(next === null ? null : latestCompletedServiceWeekStart(next.timeZone));
          }}><option value={-1}>Choose site</option>{sites.map((target, index) => <option key={target.siteId} value={index}>{target.siteLabel}</option>)}</select></label>
          {site !== null && weekStart !== null ? <div className="service-week__week" aria-label="ISO service week">
            <button type="button" className="builder__navbtn" disabled={pending} aria-label="Previous service week" onClick={() => { clearFacts(); setMessage(null); setWeekStart(addServiceWeeks(weekStart, -1)); }}>Previous</button>
            <span><strong>{formatServiceWeekRange(weekStart)}</strong><span className="service-week__zone">Mon to Sun · {site.timeZone}</span></span>
            <button type="button" className="builder__navbtn" disabled={pending || currentWeek === null || weekStart >= currentWeek} aria-label="Next service week" onClick={() => { clearFacts(); setMessage(null); setWeekStart(addServiceWeeks(weekStart, 1)); }}>Next</button>
          </div> : null}
        </div>
      ) : null}

      {message !== null ? <p role="status" className="surface__state">{message}</p> : null}
      {committedReceipt !== null ? <Receipt {...committedReceipt} /> : null}
      {busy === "targets" || targets === null ? <p role="status" className="surface__state">{serviceWeekCopy.loadingTargets}</p>
        : targets.status !== "ok" ? <p role="status" className="surface__state">{targets.status === "empty" ? serviceWeekCopy.targetsEmpty : serviceWeekReasonCopy(targets.reason)}</p>
        : busy === "review" ? <p role="status" className="surface__state">{serviceWeekCopy.loadingReview}</p>
        : site === null ? <p className="surface__state">Choose a client and site to review a service week.</p>
        : review === null ? <p role="status" className="surface__state">{serviceWeekCopy.loadingReview}</p>
        : <ReviewState review={review} />}
      {entryReview?.status === "ok" && entryReview.changeClass !== null ? <p role="status" className="service-week__change">{serviceWeekChangeCopy[entryReview.changeClass]}</p> : null}

      {facts !== null && busy !== "review" ? <>
        <p className="service-week__context">{facts.basis === "frozen_snapshot" ? "Frozen service facts" : "Current service facts"} · {facts.providerLabel} · {facts.clientLabel} · {facts.siteLabel} · {formatServiceWeekRange(facts.weekStart)} · {facts.timeZone}</p>
        {review?.status === "ok" && review.verdict === "approved" ? null : <Summary summary={facts.summary} />}
        <div className="service-week__duration"><strong>Recorded duration this week</strong><p>{recordedServiceDuration(facts.workSegments) ?? serviceWeekCopy.weekDurationMissing}</p></div>
        <FactReview key={revision ?? "frozen"} facts={facts} entries={entryReview?.status === "ok" ? entryReview.entries : []} pending={pending} onDecision={openEntry} onInclude={(candidateRef, button) => openConfirmation({ kind: "include", candidateRef }, button)} />
        {entryReview?.status === "ok" ? <>
          <section className="service-week__clean" aria-label="Review clean entries"><h4>Review clean entries</h4>
            {entryReview.cleanEntryRefs.length === 0 ? <p>{serviceWeekCopy.noCleanEntries}</p> : <button type="button" className="builder__navbtn" disabled={pending} onClick={(event) => openConfirmation({ kind: "clean" }, event.currentTarget)}>Mark {entryReview.cleanEntryRefs.length} reviewed</button>}
            <p className="service-week__muted">Rows with exceptions stay outside this act. Week approval is a separate act.</p>
          </section>
          <ServiceWeekDecisionTrail trail={entryReview.trail} timeZone={facts.timeZone} />
        </> : null}
        {current !== null ? <div className="service-week__approval">
          {current.approvalEligibility.blockerCodes.map((code) => <p key={code} role="status">{serviceWeekReasonCopy(code)}</p>)}
          <button type="button" className="minter__confirm-go" disabled={pending || !current.approvalEligibility.canApprove} onClick={(event) => openConfirmation({ kind: "approve" }, event.currentTarget)}>{serviceWeekCopy.approve}</button>
        </div> : null}
      </> : null}

      {entrySelection !== null && site !== null ? <ServiceWeekEntryDecisionDialog selection={entrySelection} timeZone={site.timeZone} pending={busy === "entry"} error={entryError} onClose={closeEntry} onSubmit={(reason, boundary) => void submitEntry(reason, boundary)} onReturnFocus={returnFocus} /> : null}

      <AlertDialog.Root open={confirmation !== null} onOpenChange={(open) => { if (!open && busyRef.current !== "include" && busyRef.current !== "approve" && busyRef.current !== "clean") { generation.current += 1; setConfirmation(null); } }}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="glass-modal schedule__alert-overlay" />
          <AlertDialog.Content className="glass-modal__panel schedule__alert-content service-week__dialog" onOpenAutoFocus={(event) => { event.preventDefault(); cancelButton.current?.focus(); }} onEscapeKeyDown={(event) => { if (busyRef.current === "include" || busyRef.current === "approve" || busyRef.current === "clean") event.preventDefault(); }} onCloseAutoFocus={(event) => {
            event.preventDefault();
            const target = opener.current?.isConnected ? opener.current : refreshButton.current;
            target?.focus();
          }}>
            <div className="glass-modal__body">
              <AlertDialog.Title>{confirmation?.kind === "include" ? serviceWeekCopy.include : confirmation?.kind === "clean" ? "Mark clean entries reviewed" : serviceWeekCopy.approve}</AlertDialog.Title>
              <AlertDialog.Description>{confirmation?.kind === "include" ? "Include this unscheduled service item in the complete service record. This does not approve payroll hours." : confirmation?.kind === "clean" ? serviceWeekCopy.bulkConfirmation : serviceWeekCopy.confirmation}</AlertDialog.Description>
              {current !== null ? <p>{current.clientLabel} · {current.siteLabel} · {formatServiceWeekRange(current.weekStart)} · {current.timeZone}</p> : null}
              {confirmation?.kind === "approve" && current !== null ? <Summary summary={current.summary} /> : null}
              <div className="minter__confirm-actions">
                <AlertDialog.Cancel asChild><button ref={cancelButton} type="button" className="builder__navbtn" disabled={pending}>Cancel</button></AlertDialog.Cancel>
                <AlertDialog.Action asChild><button type="button" className="minter__confirm-go" disabled={pending} onClick={(event) => { event.preventDefault(); void confirmAction(); }}>{confirmation?.kind === "include" ? serviceWeekCopy.include : confirmation?.kind === "clean" ? "Mark clean entries reviewed" : serviceWeekCopy.approve}</button></AlertDialog.Action>
              </div>
              {busy === "include" || busy === "approve" || busy === "clean" ? <p role="status">{busy === "include" ? "Recording inclusion..." : busy === "clean" ? serviceWeekCopy.reviewPending : "Recording approval..."}</p> : null}
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </section>
  );
}

function Summary({ summary }: { summary: ServiceWeekSummary }) {
  return <dl className="service-week__summary" aria-label="Complete service-week counts">{(Object.keys(serviceSummaryLabels) as Array<keyof ServiceWeekSummary>).map((key) => <div key={key}><dt>{serviceSummaryLabels[key]}</dt><dd>{summary[key]}</dd></div>)}</dl>;
}

function Receipt({ receipt, header }: RecordedReceipt) {
  return <section className="service-week__receipt" aria-label="Recorded service-week approval">
    <p role="status"><span className="service-week__proof" aria-hidden="true">✓ </span>{serviceWeekCopy.receipt}</p>
    <p>{header.clientLabel} · {header.siteLabel} · {formatServiceWeekRange(header.weekStart)}</p>
    <p>Recorded at <time dateTime={receipt.approvedAt}>{formatServiceWeekInstant(receipt.approvedAt, header.timeZone)}</time> · {header.timeZone}</p>
    <Summary summary={receipt.summary} />
  </section>;
}

function ReviewState({ review }: { review: ServiceWeekReview }) {
  if (review.status === "in_progress") return <p role="status" className="surface__state">{inProgressServiceWeekCopy(review.timeZone)}</p>;
  if (review.status !== "ok") return <p role="status" className="surface__state">{serviceWeekReasonCopy(review.reason)}</p>;
  if (review.verdict === "approved") return <Receipt receipt={review.receipt} header={review.review} />;
  if (review.verdict === "stale") return <>
    <p role="status" className="surface__state service-week__unavailable">{review.currentStatus === "unavailable" ? serviceWeekCopy.staleUnavailable : serviceWeekCopy.stale}</p>
    <div className="service-week__prior"><h4>Prior frozen approval</h4><Receipt receipt={review.priorReceipt} header={review.priorSnapshotHeader} /></div>
  </>;
  return <p role="status" className="surface__state">{serviceWeekCopy.unapproved}</p>;
}

function FactReview({ facts, entries, pending, onInclude, onDecision }: {
  facts: Facts;
  entries: ServiceWeekEntry[];
  onDecision: (act: ServiceEntryAct, entry: ServiceWeekEntry, button: HTMLButtonElement) => void;
  pending: boolean;
  onInclude: (candidateRef: string, button: HTMLButtonElement) => void;
}) {
  const [selectedOrdinal, setSelectedOrdinal] = useState<number | null>(null);
  const rows = useMemo(() => facts.coverage.map((row) => ({
    row,
    hasException: row.exceptionCodes.length > 0
      || facts.workSegments.some((segment) => segment.coverageRowOrdinal === row.rowOrdinal && segment.exceptionCodes.length > 0)
      || facts.checkpoints.some((checkpoint) => checkpoint.coverageRowOrdinal === row.rowOrdinal && checkpoint.validationStatus !== "device_verified"),
  })).sort((left, right) => Number(right.hasException) - Number(left.hasException) || left.row.rowOrdinal - right.row.rowOrdinal), [facts]);
  const selected = rows.find(({ row }) => row.rowOrdinal === selectedOrdinal)?.row ?? rows[0]?.row ?? null;
  const candidate = facts.basis === "current_candidates" ? facts.coverage.find((row) => row.rowOrdinal === selected?.rowOrdinal) : null;
  return <>
    <div className="service-week__split">
      <nav className="service-week__queue" aria-label="Service facts, exceptions first">
        {([true, false] as const).map((exceptions) => {
          const group = rows.filter((row) => row.hasException === exceptions);
          return group.length > 0 ? <div key={String(exceptions)}><h4>{exceptions ? "! Exceptions" : "Other service facts"} · {group.length}</h4>{group.map(({ row }) => <button key={row.rowOrdinal} type="button" className="service-week__selector" aria-current={selected?.rowOrdinal === row.rowOrdinal ? "true" : undefined} onClick={() => setSelectedOrdinal(row.rowOrdinal)}>
            <strong>{row.serviceLabel} · {row.serviceDay}</strong>
            <span>{row.kind === "scheduled_seat" ? `Scheduled seat ${row.rowOrdinal}` : "Unscheduled service"}</span>
            <ExceptionWords row={row} />
            <span>{row.verifiedCheckInCount} of {row.expectedCheckInCount} required check-ins verified</span>
          </button>)}</div> : null;
        })}
      </nav>
      <section className="service-week__detail" aria-label="Selected service facts">
        {selected !== null ? <>
          <h4>{selected.serviceLabel} · {selected.serviceDay}</h4>
          {selected.kind === "scheduled_seat" ? <><span className={`service-week__template tpl-color--${selected.templateStyleToken}`}>{selected.templateLabel}</span><p>Scheduled: {formatServiceWeekInstant(selected.scheduledStart, facts.timeZone)} to {formatServiceWeekInstant(selected.scheduledEnd, facts.timeZone)} · {facts.timeZone}</p></> : <p>Unscheduled service · {candidate?.candidateState === "pending" ? "Pending inclusion" : candidate?.candidateState === "rejected" ? "Rejected inclusion" : "Included in service week"}</p>}
          <ExceptionWords row={selected} />
          <h5>Recorded work segments</h5>
          {facts.workSegments.filter((segment) => segment.coverageRowOrdinal === selected.rowOrdinal).sort((left, right) => left.segmentOrdinal - right.segmentOrdinal).map((segment) => <article className="service-week__fact" key={segment.rowOrdinal} aria-label={`Work segment ${segment.segmentOrdinal}`}>
            <h6>Segment {segment.segmentOrdinal}{"personLabel" in segment && segment.personLabel !== null ? ` · ${segment.personLabel}` : ""}</h6>
            <p className={segment.startProvenance === "device_verified" ? "service-week__proof" : ""}>Start: {segment.recordedStart !== null ? `${formatServiceWeekInstant(segment.recordedStart, facts.timeZone)} · ` : ""}{serviceProvenanceCopy(segment.startProvenance)}</p>
            <p className={segment.endProvenance === "device_verified" ? "service-week__proof" : ""}>End: {segment.recordedEnd !== null ? `${formatServiceWeekInstant(segment.recordedEnd, facts.timeZone)} · ` : ""}{serviceProvenanceCopy(segment.endProvenance)}</p>
            {segment.startProvenance === "officer_entered" || segment.endProvenance === "officer_entered" ? <p className="service-week__muted">Officer-originated action at server receipt time · no device proof.</p> : null}
            <p>Recorded duration: <span className="service-week__duration-value">{recordedServiceDuration([segment]) ?? serviceWeekCopy.durationMissing}</span></p>
            <EntryDecisionDetail entry={entries.find((entry) => entry.rowOrdinal === segment.rowOrdinal)} timeZone={facts.timeZone} pending={pending} onDecision={onDecision} />
            <ExceptionWords row={segment} />
          </article>)}
          {!facts.workSegments.some((segment) => segment.coverageRowOrdinal === selected.rowOrdinal) ? <p>No attributable work segments.</p> : null}
          <h5>Check-ins · {selected.verifiedCheckInCount} of {selected.expectedCheckInCount} required verified</h5>
          {facts.checkpoints.filter((checkpoint) => checkpoint.coverageRowOrdinal === selected.rowOrdinal).map((checkpoint) => <article className="service-week__fact" key={checkpoint.rowOrdinal}>
            <h6>{checkpoint.routeLabel} · {checkpoint.checkpointLabel}</h6>
            <p className={checkpoint.validationStatus === "device_verified" ? "service-week__proof" : ""}>{serviceCheckpointCopy(checkpoint)}</p>
            {checkpoint.recordedAt !== null ? <p>{formatServiceWeekInstant(checkpoint.recordedAt, facts.timeZone)} · {facts.timeZone}</p> : null}
          </article>)}
          {!facts.checkpoints.some((checkpoint) => checkpoint.coverageRowOrdinal === selected.rowOrdinal) ? <p>No checkpoint facts.</p> : null}
          {candidate?.canInclude && candidate.candidateRef !== null ? <button type="button" className="builder__navbtn" disabled={pending} onClick={(event) => onInclude(candidate.candidateRef, event.currentTarget)}>{serviceWeekCopy.include}</button> : null}
        </> : <p>No coverage facts.</p>}
      </section>
    </div>
    <section className="service-week__incidents" aria-label="Included incident facts">
      <h4>Incident facts · {facts.summary.incidentCount}</h4>
      {facts.incidents.map((incident) => <article className="service-week__fact" key={incident.rowOrdinal}>
        <p>{formatServiceWeekInstant(incident.occurredAt, facts.timeZone)} · {facts.timeZone}{incident.postLabel !== null ? ` · ${incident.postLabel}` : ""}</p>
        <p>{INCIDENT_CATEGORY[incident.category]} · {INCIDENT_SEVERITY[incident.severity]} · {INCIDENT_STATE[incident.reviewState]}</p>
      </article>)}
      {facts.incidents.length === 0 ? <p>No incident facts in this service week.</p> : null}
    </section>
  </>;
}

function EntryDecisionDetail({ entry, timeZone, pending, onDecision }: { entry: ServiceWeekEntry | undefined; timeZone: string; pending: boolean; onDecision: (act: ServiceEntryAct, entry: ServiceWeekEntry, button: HTMLButtonElement) => void }) {
  if (!entry) return null;
  return <div className="service-week__entry-decisions">
    {(["start", "end"] as const).map((boundary) => {
      const original = boundary === "start" ? entry.recordedStart : entry.recordedEnd;
      const current = boundary === "start" ? entry.currentStart : entry.currentEnd;
      const provenance = boundary === "start" ? entry.startProvenance : entry.endProvenance;
      return provenance === "supervisor_set" || original !== current ? <p key={boundary}>Recorded {original === null ? "none" : formatServiceWeekInstant(original, timeZone)} · {boundary} {provenance === "supervisor_set" ? "set" : "resubmitted"} {current === null ? "none" : formatServiceWeekInstant(current, timeZone)} · reason on file</p> : null;
    })}
    {entry.decisionState === "rejected" ? <p className="service-week__change">! Rejected entry · awaiting resolution</p> : entry.decisionState === "resubmitted" ? <p className="service-week__change">Officer resubmitted · awaiting review</p> : entry.decisionState === "reviewed" ? <p>Reviewed</p> : null}
    {entry.reason !== null ? <p>{entry.reason}</p> : null}
    <div className="service-week__entry-actions">
      {entry.canReject ? <button type="button" className="builder__navbtn" disabled={pending} onClick={(event) => onDecision("reject", entry, event.currentTarget)}>{serviceWeekCopy.rejectEntry}</button> : null}
      {entry.canSet ? <>{(["set_start", "set_end"] as const).map((act) => <button key={act} type="button" className="builder__navbtn" disabled={pending} onClick={(event) => onDecision(act, entry, event.currentTarget)}>{act === "set_start" ? serviceWeekCopy.setStart : serviceWeekCopy.setEnd}</button>)}</> : null}
    </div>
  </div>;
}

function ExceptionWords({ row }: { row: Pick<ServiceWeekCoverageFact, "exceptionCodes"> }) {
  return <span className="service-week__exceptions">{row.exceptionCodes.map((code) => <span key={code}>! {serviceExceptionCopy(code)}</span>)}</span>;
}

const INCIDENT_CATEGORY = { safety: "Safety", security: "Security", maintenance: "Maintenance", client_note: "Client note", other: "Other" };
const INCIDENT_SEVERITY = { info: "Info", low: "Low", medium: "Medium", high: "High", critical: "Critical" };
const INCIDENT_STATE = { submitted: "Submitted", under_review: "Under review", locked: "Locked" };
